import { MutationHandlingServiceV2 } from '@akebono/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { of, Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';
import { AutoLotView } from 'src/app/const';
import {
  LotSource,
  LotTypeEnum,
  LotUpdateGQL,
} from 'src/app/modules/graphql/service/graphql-auto-main-service';

@Component({
  selector: 'app-lot-add-photo-modal',
  templateUrl: './lot-add-photo-modal.component.html',
  styleUrls: ['./lot-add-photo-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LotAddPhotoModalComponent implements OnInit, OnDestroy {
  @Input() lot: AutoLotView;
  @Input() lotType: LotTypeEnum;
  @Input() lotSource: LotSource;
  @Output() completed = new EventEmitter<void>();

  mode: 'upload' | 'sort' = 'upload';

  uploading = false;
  saving$ = of(false);

  useWatermark = false;
  noAuctionList = false;

  images: string[] = [];

  readonly itemHeightPx = 110;
  readonly grid = {
    xs: 12,
    sm: 8,
    md: 6,
    lg: 4,
  };

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly modalRef: NzModalRef,
    private readonly mhs: MutationHandlingServiceV2,
    private readonly lotUpdateGQL: LotUpdateGQL,
  ) {}

  ngOnInit(): void {
    const sourceImages = this.lot.images || [];
    this.images = this.clearImageUrls(sourceImages);
    this.useWatermark = sourceImages.some((url) => url.includes('/pp/') || url.includes('/qp/'));
  }

  sortCompleted(images: string[]): void {
    this.images = images;
    this.mode = 'upload';
  }

  save(): void {
    let images = this.images;

    if (this.useWatermark) {
      images = this.applyWatermarkToImageUrls(images);
    }

    const mutationRef = this.mhs.mutate(
      this.lotUpdateGQL,
      {
        input: {
          lotId: this.lot.id,
          lotType: this.lotType,
          lotSource: this.lotSource,
          photos: images,
        },
      },
      {
        successTranslationKey: 'UPLOAD_PHOTOS.SUCCESS',
        failureTranslationKey: 'UPLOAD_PHOTOS.FAIL',
      },
    );

    this.saving$ = mutationRef.loading;
    mutationRef.data.pipe(first(), takeUntil(this.destroy$)).subscribe(() => {
      this.completed.emit();
      this.cancel();
    });
  }

  cancel(): void {
    this.modalRef.destroy();
  }

  private clearImageUrls(imageUrls: string[]): string[] {
    return imageUrls.map((url) => url.replace('/pp', '').replace('/qp', ''));
  }

  private applyWatermarkToImageUrls(imageUrls: string[]): string[] {
    return imageUrls.map((url, index) => {
      const isAuctionList = !index;
      const [schema, , domain, ...path] = url.split('/');
      const watermarkPrefix = isAuctionList && !this.noAuctionList ? 'qp' : 'pp';

      if (
        domain.includes('lots-staging') ||
        domain.includes('ecarjp') ||
        domain.includes('twilight') ||
        domain.includes('akebono')
      ) {
        return [schema, '', domain, watermarkPrefix, ...path].join('/');
      } else {
        return url;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
